import { useGetTicketOptions } from '../../openapi/ticket-options/ticket-options';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types';
import { calculateOnNet, definePrice } from '../helpers/calculateShowFees';
import NumInput from '../components/NumInput';
import { useCart } from '../hooks/useCart';
import { Show } from '../../openapi/model';
import { useLang } from '../hooks/useLanguage';
import { formatCurrency } from '../helpers/formatters';
import PriceLoader from '../components/PriceLoader';

function SelectTickets({ show }: { show: Show }) {
    const { getI18n, t } = useLang();
    // priceOverrides (dynamic quote or per-timeslot custom prices) is resolved
    // once in the cart context so step 1 and step 2 show identical prices and the
    // cart re-prices on timeslot change.
    const { cart, setCart, timeslot, priceOverrides, isPricingLoading } = useCart();
    const {
        data: { ticketTypes } = { ticketTypes: [] },
        isLoading,
        isError,
        error,
    } = useGetTicketTypes({
        optionalId: show.id,
        includeSeats: true,
        onlyShowInList: true,
        onlyGuestVisible: true,
    });
    const {
        data: { ticketOptions } = { ticketOptions: [] },
        isLoading: isTicketOptionsLoading,
        isError: isTicketOptionsError,
        error: ticketOptionsError,
    } = useGetTicketOptions({ optionalId: show.id, onlyGuestVisible: true });

    if (isLoading || isTicketOptionsLoading) {
        return <div>{t('common.loading')}</div>;
    }
    if (isError || isTicketOptionsError) {
        return <div>{error?.message || ticketOptionsError?.message}</div>;
    }

    // only tickets exists in cart which have 'can buy alone' is false
    const canBuyAloneError =
        cart.length > 0 && cart.every((ticket) => ticket.canBuyAlone === false);

    const selectedSlotKey = timeslot?.slotKey ?? null;
    const visibleOptions = ticketOptions.filter(
        (opt) => !opt.slotKey || opt.slotKey === selectedSlotKey,
    );

    const hasChildOrInfant = cart.some(
        (ticket) => ticket.ageBand === 'child' || ticket.ageBand === 'infant',
    );
    const adultTicketRequiredError =
        !!show.marketing?.requireAdultTicket &&
        hasChildOrInfant &&
        !cart.some((ticket) => ticket.ageBand === 'adult');

    const allowCrossTicketBuying = !!show.marketing?.allowCrossTicketBuying;
    const lockedTicketTypeId =
        !allowCrossTicketBuying && cart.length > 0 ? cart[0].ticketTypeId : null;

    return (
        <div className="fireknife-select-tickets">
            <h2 className="fireknife-select-tickets__title">{t('widgetInfo.selectTickets')}</h2>
            {canBuyAloneError && (
                <div className="fireknife-select-tickets__error">{t('errors.cannotBuyAlone')}</div>
            )}
            {adultTicketRequiredError && (
                <div className="fireknife-select-tickets__error">
                    {t('errors.adultTicketRequired')}
                </div>
            )}
            <div className="fireknife-select-tickets__boxes">
                {ticketTypes.map((ticketType, ticketTypeIdx) => {
                    const isLocked =
                        lockedTicketTypeId !== null && lockedTicketTypeId !== ticketType.id;

                    if (isLocked) {
                        return (
                            <div className="fireknife-select-tickets__box" key={ticketType.id}>
                                <h3 className="fireknife-select-tickets__box-title">
                                    {getI18n(ticketType.name, ticketType.nameI18n)}
                                </h3>
                                <div className="fireknife-select-tickets__locked-card">
                                    <p className="fireknife-select-tickets__locked-note">
                                        {t('errors.cannotMixTicketTypes')}
                                    </p>
                                    <button
                                        type="button"
                                        className="fireknife-select-tickets__switch-btn fireknife-btn fireknife-btn--sm fireknife-btn--secondary"
                                        onClick={() => setCart([])}
                                    >
                                        {t('widgetInfo.switchTicketType')}
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="fireknife-select-tickets__box" key={ticketType.id}>
                            <h3 className="fireknife-select-tickets__box-title">
                                {getI18n(ticketType.name, ticketType.nameI18n)}
                            </h3>
                            <h5 className="fireknife-select-tickets__box-subtitle">
                                {getI18n(ticketType.description, ticketType.descriptionI18n)}
                            </h5>
                            <form className="fireknife-select-tickets__form" action="#">
                                {visibleOptions.map((ticketOption, ticketOptionIdx) => {
                                    try {
                                        // here the "name" should be in english to resolve it to the proper price
                                        // basePrice = show default; priceInCents = active price
                                        // (dynamic/timeslot override when present, else base).
                                        const basePrice = definePrice(
                                            ticketType.priceMatrix,
                                            ticketOption.name,
                                        );
                                        const overridePrice = priceOverrides.get(
                                            `${ticketType.id}:${ticketOption.id}`,
                                        );
                                        const priceInCents =
                                            overridePrice !== undefined ? overridePrice : basePrice;

                                        const finalPrice =
                                            show.priceListingMode === 'net'
                                                ? { finalPrice: priceInCents }
                                                : calculateOnNet(
                                                      priceInCents,
                                                      show.fees || [],
                                                      true,
                                                  );

                                        // ICartItem
                                        const ticketInCart = cart.findIndex(
                                            (ticket) =>
                                                ticket.ticketTypeId === ticketType.id &&
                                                ticket.ticketOptionId === ticketOption.id,
                                        );

                                        return (
                                            <div
                                                className="fireknife-select-tickets__block"
                                                key={`${ticketTypeIdx}-${ticketOptionIdx}`}
                                            >
                                                <NumInput
                                                    label={`${getI18n(ticketOption.name, ticketOption.nameI18n)}`}
                                                    description={getI18n(
                                                        ticketOption.description,
                                                        ticketOption.descriptionI18n,
                                                    )}
                                                    subLabel={
                                                        isPricingLoading ? (
                                                            <PriceLoader />
                                                        ) : (
                                                            formatCurrency(
                                                                finalPrice.finalPrice / 100,
                                                            )
                                                        )
                                                    }
                                                    value={
                                                        ticketInCart !== -1
                                                            ? cart[ticketInCart].quantity
                                                            : 0
                                                    }
                                                    setValue={(newCount: number) => {
                                                        if (isPricingLoading) return;
                                                        let newCart = [...cart];
                                                        if (ticketInCart !== -1) {
                                                            newCart[ticketInCart].quantity =
                                                                newCount;
                                                        } else {
                                                            newCart.push({
                                                                ticketTypeId: ticketType.id,
                                                                ticketTypeName: ticketType.name,
                                                                ticketTypeNameI18n:
                                                                    ticketType.nameI18n,
                                                                ticketOptionId: ticketOption.id,
                                                                ticketOptionName: ticketOption.name,
                                                                ticketOptionNameI18n:
                                                                    ticketOption.nameI18n,
                                                                price: priceInCents,
                                                                basePrice,
                                                                quantity: newCount,
                                                                customFields:
                                                                    ticketType.customFields,
                                                                canBuyAlone: ticketType.canBuyAlone,
                                                                ageBand:
                                                                    ticketOption.ageBand ?? null,
                                                            });
                                                        }
                                                        newCart = newCart.filter(
                                                            (e) => e.quantity > 0,
                                                        );
                                                        setCart(newCart);
                                                    }}
                                                    disabled={isPricingLoading}
                                                />
                                            </div>
                                        );
                                    } catch (_e) {
                                        return null;
                                    }
                                })}
                            </form>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default SelectTickets;
